<template>
  <div
    v-if="permission != 'n'"
    id="ht-opinion"
    v-loading="loading"
    class="ht-opinion"
    :class="{ 'is-print': isInPrint }"
    :style="additionalStyle"
  >
    <el-table :data="rows" :max-height="maxHeight">
      <el-table-column
        width="50"
        :label="$t('eip.common.index')"
        align="center"
        type="index"
      />
      <el-table-column
        :label="$t('page.htOpinion.approvalNode')"
        prop="taskName"
        :width="widthMap.taskName"
        :show-overflow-tooltip="!isInPrint"
      />
      <el-table-column
        align="left"
        prop="displayApproverNames"
        :label="$t('page.approvalButtonMixin.reviewer')"
        :width="widthMap.qualfiedNames"
        show-overflow-tooltip
      >
        <template #default="{ row: { displayApproverNames } }">
          <el-tooltip
            v-if="
              !isInPrint &&
              displayApproverNames &&
              displayApproverNames.length > 0
            "
            :content="displayApproverNames"
            placement="top"
          >
            <span>{{ displayApproverNames || '-' }}</span>
          </el-tooltip>
          <span v-else>{{ displayApproverNames || '-' }}</span>
        </template>
      </el-table-column>

      <el-table-column
        align="left"
        :label="$t('page.htOpinion.handlingSituation')"
        :width="widthMap.handlingSituation"
      >
        <template #default="{ row: { isRead, statusVal, status } }">
          {{
            status !== 'awaiting_check'
              ? statusVal
              : isRead == 0
              ? $t('eip.status.toBeRead')
              : $t('eip.status.read')
          }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('page.htOpinion.handlingOpinions')"
        :width="widthMap.opinion"
        :show-overflow-tooltip="!isInPrint"
        align="left"
        class-name="opinion-column"
      >
        <template #default="{ row: { opinion, filesArray } }">
          <span class="opinion">{{ opinion || '-' }}</span>

          <div v-if="filesArray && filesArray.length" class="appendix">
            <div class="title">{{ $t('page.htOpinion.attachment') }}</div>

            <div class="file-content">
              <div
                v-for="(item, index) in filesArray"
                :key="index"
                class="attachment"
              >
                <div class="file-container">
                  <el-link
                    class="filelink"
                    :underline="false"
                    type="primary"
                    @click="search(item)"
                  >
                    {{ item.name }}
                  </el-link>
                </div>
                <i
                  class="iconfont icon-bpm-fujiandownload record-icon-file download-btn"
                  @click="downloadAttachments(item)"
                />
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="recipients"
        align="left"
        :label="$t('eip.flow.recipient')"
        :width="widthMap.recipients"
        class-name="recipients"
      >
        <template #default="{ row: { recipients } }">
          <el-tooltip placement="top" :disabled="isInPrint">
            <div slot="content">
              <template v-for="item in recipients">
                <span>{{ item.displayName }}</span>
                <span>{{ item.remark }}</span>
              </template>
            </div>
            <span v-if="recipients.length" class="text-ellipsis">
              <template v-for="item in recipients">
                <span>{{ item.displayName }}</span>
                <span>{{ item.remark }}</span>
              </template>
            </span>
            <span v-else>-</span>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('page.htOpinion.operatingTime')"
        prop="completeTime"
        :show-overflow-tooltip="!isInPrint"
        align="left"
        :width="widthMap.completeTime"
      >
        <template #default="{ row }">
          {{ row.completeTime | timezoneDateFormat }}
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('page.htOpinion.lengthOfStay')"
        :show-overflow-tooltip="!isInPrint"
        :width="widthMap.lengthOfStay"
      >
        <template #default="{ row }">
          {{ formatTimeLag(row) }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>
<script>
  import { getOpinionsById2 } from '@/api/storeProcess'
  import { downloadFile } from '@/api/portal.js'
  import moment from 'moment'
  import moment_tz from 'moment-timezone'

  import { flexColumnWidth } from '@/utils/table'
  import { STATUS_MAP } from '@/components/flow/const'

  export default {
    name: 'HtOpinion',
    props: {
      permission: {
        type: String,
        default: 'w',
        validator: function (value) {
          return ['b', 'w', 'r', 'n'].indexOf(value) !== -1
        },
      },
      flowData: {
        type: Object,
        default: () => {
          return {}
        },
      },
    },
    data() {
      return {
        loading: false,
        rows: [], // 表格数据
        widthMap: {
          taskName: '100',
          opinion: 'auto',
          qualfiedNames: '120',
          auditorName: '110',
          handlingSituation: '100',
          recipients: 150,
          completeTime: '170',
          lengthOfStay: 130,
        },
      }
    },

    computed: {
      isInPrint() {
        let parent = this.$parent

        while (parent) {
          if (parent?.$options?.name === 'FormPrintDialog') {
            return true
          }
          parent = parent.$parent
        }

        return false
      },

      maxHeight() {
        return this.isInPrint ? 'auto' : 450
      },

      additionalStyle() {
        if (!this.isInPrint) return null
        const isInFormItem = this.$parent?.$options?.name === 'ElFormItem'

        return this.isInPrint && isInFormItem
          ? {
              marginTop: '30px', // 用于打印时的样式调整
              marginLeft: `-${this.$parent.labelWidth}`, // 用于打印时的样式调整
            }
          : null
      },
    },

    mounted() {
      this.loadData()
    },
    methods: {
      search(item) {
        this.$preview(item)
      },
      downloadAttachments(item) {
        downloadFile(item.response?.fileId, item.response?.fileName)
      },
      async loadData() {
        this.loading = true

        try {
          const response = await getOpinionsById2({
            ...this.flowData,
          })

          if (this.isInPrint) {
            this.widthMap.taskName = flexColumnWidth({
              strOrFn: 'taskName',
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 32 + 2,
            })

            this.widthMap.qualfiedNames = flexColumnWidth({
              strOrFn: 'displayApproverNames',
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 24 + 2,
            })

            this.widthMap.handlingSituation = flexColumnWidth({
              strOrFn: (row) => {
                const { isRead, statusVal, status } = row
                return status === 'awaiting_check'
                  ? isRead === 0
                    ? this.$t('eip.status.toBeRead')
                    : this.$t('eip.status.read')
                  : statusVal
              },
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 32 + 2,
            })

            this.widthMap.completeTime = flexColumnWidth({
              strOrFn: (row) => {
                const { completeTime } = row
                if (!completeTime) {
                  return ''
                }

                let a = moment_tz.tz.guess()

                // 返回出 年-月-日 时:分:秒
                return moment_tz
                  .utc(moment_tz.utc(completeTime))
                  .tz(a)
                  .format('YYYY-MM-DD HH:mm:ss')
              },
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 32 + 2,
            })

            this.widthMap.recipients = flexColumnWidth({
              strOrFn: (row) => {
                if (row.recipients && row.recipients.length) {
                  return row.recipients
                    .map((item) => `${item.displayName}${item.remark}`)
                    .join('')
                }
                return '-'
              },
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 24 + 2,
            })

            this.widthMap.lengthOfStay = flexColumnWidth({
              strOrFn: (row) => this.formatTimeLag(row),
              tableData: response,
              font: '8px SourceHanSansCN',
              minWidth: 24 + 24 + 2,
            })
          }

          response.map((e) => {
            //有可能出现e.files是"\"\""的情况
            if (e.files && JSON.parse(e.files)) {
              e.filesArray = JSON.parse(JSON.parse(e.files))
            }
            // 审批人：优先取resourceNames，resourceNames值为空就取executorNames
            e.displayApproverNames = e.resourceNames || e.executorNames
            // 处理接收人数据
            if (e.recipients) {
              e.recipients = e.recipients.map((recipient) => {
                // 接收人：优先使用 targetNodeName + (targetNames)，为空则沿用旧逻辑
                const targetNodeName =
                  recipient.targetNodeName || e.targetNodeName || ''
                const targetNames = e.targetNames || ''
                const qualfiedNames = recipient.qualfiedNames || ''
                const taskName = recipient.taskName || ''
                return {
                  ...recipient,
                  displayName: targetNodeName
                    ? `${targetNodeName}${
                        targetNames ? `(${targetNames})` : ''
                      }`
                    : `${taskName}${
                        targetNames
                          ? `(${targetNames})`
                          : qualfiedNames
                          ? `(${qualfiedNames})`
                          : ''
                      }`,
                }
              })
            }
          })

          this.rows = response
        } catch (e) {
          console.error('加载数据失败:', e)
        } finally {
          this.loading = false
        }
      },
      getStyle(status) {
        const defaultStyle = {
          color: '#0560FD',
          backgroundColor: '#E9F1FF',
        }
        const style =
          STATUS_MAP.find((item) => item.status.includes(status))?.style || {}
        return {
          ...defaultStyle,
          ...style,
        }
      },
      //持续时间
      formatTimeLag(row) {
        let endTimes
        if (!row.completeTime) {
          endTimes = new moment()
        } else {
          endTimes = moment_tz.utc(row.completeTime)
        }

        let createTime = moment_tz.utc(row.createTime)
        let duration = moment.duration(endTimes.diff(createTime))

        let years = duration.years()
        if (years > 0) {
          return this.$t('eip.common.moreThenMonth', { year: years })
        }

        let months = duration.months()
        if (months > 0) {
          return this.$t('eip.common.moreThenMonth', { month: months })
        }

        let days = duration.days()
        if (days > 0) {
          return days + this.$t('eip.common.day')
        }

        let hours = duration.hours()
        if (hours > 0) {
          return hours + this.$t('eip.common.hour')
        }

        let minutes = duration.minutes()
        if (minutes > 0) {
          return minutes + this.$t('eip.common.minute')
        }

        return this.$t('eip.common.lessThen1Min')
      },
    },
  }
</script>
<style lang="scss" scoped>
  .ht-opinion {
    ::v-deep {
      .el-table {
        .el-table__header-wrapper {
          thead {
            th .cell {
              color: #5f737f !important;
            }
          }
        }

        .opinion-column {
          .cell {
            display: flex;
            flex-direction: column;

            .opinion {
              word-break: normal;
              white-space: pre-wrap;
            }

            .appendix {
              display: flex;
              flex-direction: column;
              padding: 5px 0px;
              text-align: left;

              .title {
                display: block;
                font-weight: bold;
                color: #a2a2a2;
              }

              .file-content {
                flex: 1;
                min-width: 0;
                max-width: 100% !important;
              }

              .attachment {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                margin-bottom: 10px;
                cursor: pointer;
              }

              .file-container {
                flex: 1;
                min-width: 0;
                max-width: 100% !important;
                margin-right: 10px;
                overflow: hidden;
                text-align: left;
              }
              .file-container.filelink {
                max-width: 100%;
                overflow: hidden;
              }
            }
          }
        }
      }

      .el-tag {
        display: inline-block;
        max-width: 100%;
        height: 24px;
        padding: 0 15px;
        overflow: hidden;
        font-weight: 600;
        line-height: 24px;
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
        border: none;
        border-radius: 15px;
      }

      .el-tooltip {
        display: inline-block;
      }
    }
  }

  .record-icon-file {
    font-size: 24px;
    color: #0560fd;
    text-align: right;
    cursor: pointer;
  }

  ::v-deep .el-table .cell.el-tooltip {
    display: flex;
    min-width: 50px;
  }

  ::v-deep .el-table .cell {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    padding-right: 10px;
    padding-left: 10px;
    overflow: hidden;
    line-height: 23px;
    text-overflow: ellipsis;
    word-break: break-all;
    white-space: nowrap;
  }

  .filelink {
    display: inline-block !important;
    width: 100% !important;
    overflow: hidden !important;
    text-align: left !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  .filelink .el-link--inner {
    width: 100% !important;
    overflow: hidden !important;
    text-align: left !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  // 接收人列文本超出显示省略号
  .text-ellipsis {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 打印模式样式
  .is-print {
    ::v-deep {
      .el-table {
        border-collapse: collapse;
        border: 1px solid #ebeef5;
      }

      .el-table th,
      .el-table td {
        padding: 8px 10px;
        border: 1px solid #ebeef5;
      }

      .el-table th {
        font-weight: bold;
        background-color: #f5f7fa;
      }
    }
  }
</style>
